import cn from "classnames"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { ReactComponent as NoneBorder } from "@/images/signomatic/borders/none.svg"
import { ReactComponent as RetroBorder } from "@/images/signomatic/borders/retro.svg"
import { ReactComponent as RoundedBorder } from "@/images/signomatic/borders/rounded.svg"
import { ReactComponent as ThickBorder } from "@/images/signomatic/borders/thick.svg"
import { ReactComponent as ThinBorder } from "@/images/signomatic/borders/thin.svg"
import { ReactComponent as ColorIcon } from "@/images/signomatic/color.svg"
import AutoGrid from "@/signomatic/components/auto_grid"
import Toolbox from "@/signomatic/components/toolbox"
import { type ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"

const reason = (type: string | null) => {
  switch (type) {
    case "shape":
      return "toolbox.border_disabled_shape"
    case "fixmethod":
      return "toolbox.border_disabled_fix_method"
    default:
      return ""
  }
}

const icons: IconMap = {
  none: NoneBorder,
  thin: ThinBorder,
  thick: ThickBorder,
  rounded: RoundedBorder,
  retro: RetroBorder,
}

interface BorderProps {
  name: string
  enabled: boolean
  active: boolean
  update: () => void
}

const Border = ({ name, enabled, active, update }: BorderProps) => {
  const { t } = useTranslation()

  return (
    <div className={cn("item", "selection", `border-${name}`, { disabled: !enabled, active })} onClick={update}>
      <div className="image">{icons[name] ? React.createElement(icons[name]) : ""}</div>
      <div className="name paragraph-2xs center mb-0">{t(`border.${name}`)}</div>
    </div>
  )
}

interface BorderToolboxState {
  currentSignData: SignData | null
  signState: SignState | null
}

const BorderToolbox: React.FC<ToolboxStripItemProps> = ({ sign }) => {
  const { t } = useTranslation()
  const [disabledMessage, setDisabledMessage] = useState<string>()
  const { signState, currentSignData } = useSelector<State, BorderToolboxState>(({ signState, currentSignData }) => ({
    signState,
    currentSignData,
  }))
  const dispatch = useDispatch()

  if (!signState || !currentSignData) {
    return null
  }

  const hasBorder = ![null, "none"].includes(signState.border)

  return (
    <Toolbox tKey="border">
      <React.Fragment>
        <div className="inline-toolbox">
          <AutoGrid>
            {currentSignData.borders.map(border => {
              const active = signState.border === border.name

              if (border.filters) {
                if ((border.filters.hidden && !active) || (border.filters.legacy && !active)) {
                  return null
                }
              }

              return (
                <Border
                  key={border.name}
                  name={border.name}
                  active={active}
                  enabled={border.enabled || false}
                  update={() => {
                    if (border.enabled) {
                      sign.updateState({ border: border.name })
                      setDisabledMessage(undefined)
                    } else if (border.disabled_reason) {
                      setDisabledMessage(border.disabled_reason[0])
                    }
                  }}
                />
              )
            })}
            {currentSignData.multicolor && (
              <div
                className={cn("item", "selection", "color", { disabled: !hasBorder })}
                onClick={() => {
                  if (!hasBorder) {
                    return
                  }

                  dispatch(actions.addSubToolbox("color", "border"))
                }}
              >
                <div className="image">
                  <ColorIcon />
                </div>
                <div className="name paragraph-2xs center mb-0">{t("toolbox.border_color")}</div>
              </div>
            )}
          </AutoGrid>
          {disabledMessage && (
            <div className="disabled-reason paragraph-2xs bold mb-0">{t(reason(disabledMessage))}</div>
          )}
        </div>
      </React.Fragment>
    </Toolbox>
  )
}

export default BorderToolbox
